'use client';

/**
 * A dialog an APP asked for gives focus back to that app when it closes
 * (round 7, lane 4, item 1).
 *
 * The round 6 dev run (2026-09-27, finding 8) needed two clicks for every
 * first action in Revent after focus had been in the portal: menus, Inbox,
 * Add all, Submit. Measured in Chromium with a two-origin harness (a parent
 * page and a frame whose `window` focus handler repaints synchronously, the
 * way Revent's `refreshOnReturn` does): a click into a frame whose document
 * does not hold focus fires `pointerdown`, `mousedown`, then the frame
 * window's `focus` event, then `pointerup` and `mouseup` on the REPAINTED
 * node, and no `click` at all, because the node the press began on is gone.
 * The second click has no focus event to fire and lands. When the host puts
 * focus back in the frame first (`frame.focus()`), the app's focus handler
 * runs then, and the next click lands the first time, even against an app
 * that still repaints on focus.
 *
 * The portal's part in it: every dialog an app opens (share, the inbox
 * consent, the file pickers, the agent and email asks) is portal chrome that
 * takes focus (most autofocus a control) and none gave it back, so after
 * Close focus sat on the portal's `<body>` and the person's next click was
 * the one that moved it into the app. Returning focus to the frame is also
 * what a keyboard user needs: the dialog was opened from inside the app, so
 * Escape should land them back in it, not at the top of the portal. The app
 * half (never replace the pressed node inside a focus handler) is the app's
 * to fix; Revent's is `watchReturn` in public/widgets/revent/lib/registration.js,
 * whose focus listener repaints the column and the panel synchronously.
 *
 * Rules, all in `returnFocusToAppFrame`: only a frame that is still in the
 * page, only when focus has fallen to the body (never taken back from a
 * portal field or another app the person moved to), and `preventScroll`,
 * because focusing an element inside the canvas's clipped, transformed
 * viewport would otherwise scroll it.
 * tests/unit/workspaces/r7-app-dialog-focus-return.test.tsx
 */

import { useCallback, useEffect, useRef } from 'react';

/** The app frame holding focus in `doc` right now, or null. */
export function focusedAppFrame(doc: Document = document): HTMLIFrameElement | null {
  const active = doc.activeElement;
  return active instanceof HTMLIFrameElement ? active : null;
}

/**
 * Put focus back in `frame` when nothing else holds it. True when it did.
 */
export function returnFocusToAppFrame(
  frame: HTMLIFrameElement | null,
  doc: Document = document,
): boolean {
  if (!frame || !frame.isConnected) return false;
  const active = doc.activeElement;
  if (active && active !== doc.body && active !== doc.documentElement) return false;
  try {
    frame.focus({ preventScroll: true });
  } catch {
    return false;
  }
  return doc.activeElement === frame;
}

/**
 * For a host that draws a dialog an app asked for. Call the returned
 * `capture()` synchronously when the request arrives, before the dialog
 * renders and takes focus; when `open` turns false again, the frame that held
 * focus at that moment gets it back (see `returnFocusToAppFrame`).
 */
export function useAppFrameFocusReturn(open: boolean): () => void {
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const capture = useCallback(() => {
    // A second request while a dialog is up keeps the first frame: that is
    // where the person was before any of it.
    if (!frameRef.current) frameRef.current = focusedAppFrame();
  }, []);
  useEffect(() => {
    if (open) return;
    // Runs after the dialog's DOM is gone, so a focus that was inside it has
    // already fallen to the body.
    const frame = frameRef.current;
    frameRef.current = null;
    if (frame) returnFocusToAppFrame(frame);
  }, [open]);
  return capture;
}
